import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { useApp } from "../state/AppContext";
import { useThreads, useMailboxSync } from "../state/hooks";
import { Sidebar } from "./Sidebar";
import { ThreadList } from "./ThreadList";
import { ReadingPane } from "./ReadingPane";
import { Compose } from "./Compose";
import { Settings, SETTINGS_PANES, type Pane } from "./Settings";
import { CommandPalette, type Command } from "./CommandPalette";
import { SpamReminder, SpamScreeningOverlay } from "./SpamScreening";
import type { ThreadListItem } from "../../shared/types";

interface SpamRoutine {
  enabled: boolean;
  waitingCount: number;
}

const NAV_VIEWS: { key: string; label: string }[] = [
  { key: "inbox", label: "Inbox" },
  { key: "now", label: "Needs you now" },
  { key: "vendor", label: "Waiting on vendor" },
  { key: "fyi", label: "FYI" },
  { key: "snoozed", label: "Snoozed" },
  { key: "drafts", label: "Drafts" },
  { key: "sent", label: "Sent" },
  { key: "archive", label: "Archive" },
  { key: "spam", label: "Spam" },
  { key: "trash", label: "Trash" },
  { key: "all", label: "All Mail" },
];

export function Shell() {
  const { currentMailbox, signOut, me } = useApp();
  const [view, setView] = useState("inbox");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<ThreadListItem | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsPane, setSettingsPane] = useState<Pane | undefined>(undefined);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [replyTrigger, setReplyTrigger] = useState(0);
  const [labelTrigger, setLabelTrigger] = useState(0);
  const [spamReminder, setSpamReminder] = useState<SpamRoutine | null>(null);
  const [spamQueue, setSpamQueue] = useState<ThreadListItem[] | null>(null);

  const { threads, loading, error, reload } = useThreads(currentMailbox?.id ?? null, view, query);
  const { stillSyncing, importProgress, syncNow } = useMailboxSync(currentMailbox?.id ?? null, reload);
  const lastIndexRef = useRef(0);
  const autoAdvance = me?.prefs.autoAdvance ?? true;

  useEffect(() => {
    setSelected(null);
  }, [currentMailbox?.id, view]);

  useEffect(() => {
    if (!selected) return;
    const idx = threads.findIndex((t) => t.id === selected.id);
    if (idx >= 0) {
      lastIndexRef.current = idx;
      return;
    }
    if (autoAdvance && threads.length > 0) {
      setSelected(threads[Math.min(lastIndexRef.current, threads.length - 1)]!);
    }
    // if autoAdvance is off, or the list is now empty, leave the reading
    // pane showing whatever was last selected.
  }, [threads, selected, autoAdvance]);

  useEffect(() => {
    if (!currentMailbox) return;
    api.get<SpamRoutine>(`/api/mailboxes/${currentMailbox.id}/settings/spam-routine`).then((r) => {
      if (r.enabled && r.waitingCount > 0) setSpamReminder(r);
    });
  }, [currentMailbox]);

  async function startScreening() {
    if (!currentMailbox) return;
    setSpamReminder(null);
    const res = await api.get<{ threads: ThreadListItem[] }>(`/api/mailboxes/${currentMailbox.id}/threads?view=spam`);
    setSpamQueue(res.threads);
  }

  function openSettings(pane?: Pane) {
    setSettingsPane(pane);
    setSettingsOpen(true);
  }

  async function threadAction(action: string) {
    if (!currentMailbox || !selected) return;
    await api.post(`/api/mailboxes/${currentMailbox.id}/threads/${selected.id}/action`, { action });
    reload();
  }

  function selectByOffset(offset: number) {
    if (!selected) {
      if (threads[0]) setSelected(threads[0]);
      return;
    }
    const idx = threads.findIndex((t) => t.id === selected.id);
    const next = threads[idx + offset];
    if (next) setSelected(next);
  }

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const inField = target.tagName === "INPUT" || target.tagName === "TEXTAREA";
      if (inField && !(e.metaKey || e.ctrlKey) && e.key !== "Escape") return;

      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      if (e.key === "," && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        openSettings(undefined);
        return;
      }
      if (inField) {
        if (e.key === "Escape") {
          setComposeOpen(false);
          setSettingsOpen(false);
          setPaletteOpen(false);
        }
        return;
      }

      if (e.key === "c") setComposeOpen(true);
      else if (e.key === "r") setReplyTrigger((n) => n + 1);
      else if (e.key === "l") setLabelTrigger((n) => n + 1);
      else if (e.key === "e") threadAction("archive");
      else if (e.key === "Delete" || e.key === "#") threadAction("trash");
      else if (e.key === "j") selectByOffset(1);
      else if (e.key === "w") selectByOffset(-1);
      else if (e.key === "/") {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('input[placeholder^="Search mail"]')?.focus();
      } else if (e.key === "Escape") {
        setComposeOpen(false);
        setSettingsOpen(false);
        setPaletteOpen(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, threads],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown]);

  const commands: Command[] = useMemo(() => {
    const cmds: Command[] = NAV_VIEWS.map((v) => ({ id: `view:${v.key}`, label: `Go to ${v.label}`, run: () => setView(v.key) }));
    cmds.push({ id: "compose", label: "Compose", hint: "C", run: () => setComposeOpen(true) });
    for (const p of SETTINGS_PANES) {
      cmds.push({ id: `settings:${p.key}`, label: `Settings — ${p.label}`, run: () => openSettings(p.key) });
    }
    if (selected) {
      cmds.push({ id: "thread:reply", label: "Reply to selected thread", hint: "R", run: () => setReplyTrigger((n) => n + 1) });
      cmds.push({ id: "thread:archive", label: "Archive selected thread", hint: "E", run: () => threadAction("archive") });
      cmds.push({ id: "thread:trash", label: "Delete selected thread", run: () => threadAction("trash") });
      cmds.push({ id: "thread:spam", label: "Mark selected thread as spam", run: () => threadAction("spam") });
    }
    cmds.push({ id: "screen-spam", label: "Screen spam now", run: () => startScreening() });
    cmds.push({ id: "sign-out", label: "Sign out", run: () => signOut() });
    return cmds;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, signOut, currentMailbox]);

  if (!currentMailbox) {
    return (
      <div className="gm-shell" style={{ alignItems: "center", justifyContent: "center", color: "var(--gm-text-4)" }}>
        No mailbox connected yet.
      </div>
    );
  }

  return (
    <div className="gm-shell">
      <Sidebar view={view} onSelectView={setView} onCompose={() => setComposeOpen(true)} onOpenSettings={() => openSettings(undefined)} />
      <ThreadList
        view={view}
        query={query}
        onQueryChange={setQuery}
        threads={threads}
        loading={loading}
        error={error}
        stillSyncing={stillSyncing}
        importProgress={importProgress}
        selectedId={selected?.id ?? null}
        onSelect={setSelected}
        onRefresh={() => syncNow(true)}
      />
      <ReadingPane mailboxId={currentMailbox.id} threadId={selected?.id ?? null} onAfterAction={reload} replyTrigger={replyTrigger} labelTrigger={labelTrigger} />
      {composeOpen && <Compose mailboxId={currentMailbox.id} onClose={() => setComposeOpen(false)} />}
      {settingsOpen && <Settings onClose={() => setSettingsOpen(false)} initialPane={settingsPane} />}
      {paletteOpen && <CommandPalette commands={commands} onClose={() => setPaletteOpen(false)} />}
      {spamReminder && (
        <SpamReminder
          waitingCount={spamReminder.waitingCount}
          onScreen={startScreening}
          onRemindLater={() => {
            setSpamReminder(null);
            setTimeout(() => setSpamReminder(spamReminder), 60 * 60 * 1000);
          }}
          onSkip={() => setSpamReminder(null)}
        />
      )}
      {spamQueue && (
        <SpamScreeningOverlay
          mailboxId={currentMailbox.id}
          queue={spamQueue}
          onDone={() => {
            setSpamQueue(null);
            reload();
          }}
        />
      )}
    </div>
  );
}
